import React, { useState, useEffect, useMemo } from 'react';
import { Shield, Plus, Users, UserCheck, UserX, Calendar, Mail, FileText, Upload, CheckCircle, Clock, AlertTriangle, Search, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, RefreshCw, CalendarPlus, XCircle, Key, Copy, Trash2, Tag, Eye, Download, File, FolderOpen, RotateCcw, Ban, ShieldCheck, Link2, Check } from 'lucide-react';
import { authApi, adminApi, fileRequestApi, apiTokenApi, uploadApi } from '../services/api';

export function AdminPage() {
  const [users, setUsers] = useState([]);
  const [fileRequests, setFileRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'requests'

  // Pagination state
  const [usersPage, setUsersPage] = useState(1);
  const [requestsPage, setRequestsPage] = useState(1);
  const itemsPerPage = 10;

  // Sorting state
  const [usersSortField, setUsersSortField] = useState('created_at');
  const [usersSortDirection, setUsersSortDirection] = useState('desc');
  const [requestsSortField, setRequestsSortField] = useState('created_at');
  const [requestsSortDirection, setRequestsSortDirection] = useState('desc');

  // Filter state
  const [usersSearchTerm, setUsersSearchTerm] = useState('');
  const [usersRoleFilter, setUsersRoleFilter] = useState('all');
  const [usersStatusFilter, setUsersStatusFilter] = useState('all');

  const [requestsSearchTerm, setRequestsSearchTerm] = useState('');
  const [requestsStatusFilter, setRequestsStatusFilter] = useState('all');

  // API Token state
  const [apiTokens, setApiTokens] = useState([]);
  const [showCreateTokenForm, setShowCreateTokenForm] = useState(false);
  const [newlyCreatedToken, setNewlyCreatedToken] = useState(null);
  const [newToken, setNewToken] = useState({
    name: '',
    expires_in_days: '',
    scope: 'user',
    embed_primary_color: '#000000',
    embed_header_text: '',
    embed_show_logo: true,
  });

  // File viewing state
  const [viewingFiles, setViewingFiles] = useState(null);
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);

  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    role: 'user'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [currentUserData, usersData, requestsData, tokensData] = await Promise.all([
          authApi.getCurrentUser(),
          adminApi.getAllUsers(),
          adminApi.getAllFileRequests(),
          apiTokenApi.getAll().catch(() => []),
        ]);
        setCurrentUser(currentUserData);
        setUsers(usersData);
        setFileRequests(requestsData);
        setApiTokens(tokensData);
      } catch (err) {
        setError('Failed to load admin data');
        console.error('Admin page error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const createdUser = await adminApi.createUser(newUser);
      setUsers([createdUser, ...users]);
      setNewUser({ username: '', email: '', role: 'user' });
      setShowCreateForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleToggleReusable = async (requestId, currentValue) => {
    try {
      const result = await fileRequestApi.toggleReusable(requestId, !currentValue);
      // Update the local state
      setFileRequests(prev => prev.map(req =>
        req.id === requestId
          ? { ...req, is_reusable: result.is_reusable, status: result.status }
          : req
      ));
    } catch (err) {
      console.error('Failed to toggle reusable status:', err);
      setError(err.response?.data?.detail || 'Failed to update reusable status');
    }
  };

  const [copiedLink, setCopiedLink] = useState(null);
  const [extendingRequest, setExtendingRequest] = useState(null);
  const [extendHours, setExtendHours] = useState(72);

  const handleCopyLink = async (uploadUrl, requestId) => {
    try {
      await navigator.clipboard.writeText(uploadUrl);
      setCopiedLink(requestId);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleExtendExpiration = async (requestId) => {
    try {
      const result = await fileRequestApi.extendExpiration(requestId, extendHours);
      // Update the local state
      setFileRequests(prev => prev.map(req =>
        req.id === requestId
          ? { ...req, expires_at: result.expires_at, status: result.status }
          : req
      ));
      setExtendingRequest(null);
      setExtendHours(72);
    } catch (err) {
      console.error('Failed to extend expiration:', err);
      setError(err.response?.data?.detail || 'Failed to extend expiration');
    }
  };

  const handleCreateToken = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        name: newToken.name,
        scope: newToken.scope,
        embed_primary_color: newToken.embed_primary_color,
        embed_header_text: newToken.embed_header_text,
        embed_show_logo: newToken.embed_show_logo,
      };
      if (newToken.expires_in_days) {
        payload.expires_in_days = parseInt(newToken.expires_in_days, 10);
      }
      const created = await apiTokenApi.create(payload);
      setNewlyCreatedToken(created.raw_token);
      setApiTokens(prev => [created, ...prev]);
      setNewToken({ name: '', expires_in_days: '', scope: 'user', embed_primary_color: '#000000', embed_header_text: '', embed_show_logo: true });
      setShowCreateTokenForm(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create API token');
    }
  };

  const handleRevokeToken = async (tokenId) => {
    try {
      await apiTokenApi.revoke(tokenId);
      setApiTokens(prev => prev.map(t => t.id === tokenId ? { ...t, is_active: false } : t));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to revoke API token');
    }
  };

  const handleViewFiles = async (requestId) => {
    setFilesLoading(true);
    setViewingFiles(requestId);
    try {
      const filesData = await fileRequestApi.getFiles(requestId);
      setFiles(filesData);
    } catch (err) {
      console.error('Failed to load files:', err);
      setError('Failed to load files');
    } finally {
      setFilesLoading(false);
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await uploadApi.download(fileId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download file');
    }
  };

  // User management actions
  const [confirmAction, setConfirmAction] = useState(null); // { type, userId, userName }
  const [actionLoading, setActionLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleChangeRole = async (userId, newRole) => {
    setActionLoading(userId);
    try {
      const updated = await adminApi.updateUserRole(userId, newRole);
      setUsers(prev => prev.map(u => u.id === userId ? updated : u));
      setSuccessMessage(`Role updated to ${newRole}`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (userId) => {
    setActionLoading(userId);
    try {
      await adminApi.resetUserPassword(userId);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, password_set: false } : u));
      setSuccessMessage('Password reset email sent');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const handleToggleStatus = async (userId, currentlyActive) => {
    setActionLoading(userId);
    try {
      const updated = await adminApi.updateUserStatus(userId, !currentlyActive);
      setUsers(prev => prev.map(u => u.id === userId ? updated : u));
      setSuccessMessage(currentlyActive ? 'User suspended' : 'User activated');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user status');
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(userId);
    try {
      await adminApi.deleteUser(userId);
      setUsers(prev => prev.filter(u => u.id !== userId));
      setSuccessMessage('User deleted');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setActionLoading(null);
      setConfirmAction(null);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const getTokenStatusBadge = (token) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    if (!token.is_active) return <span className={`${baseClasses} bg-red-100 text-red-800`}>Revoked</span>;
    if (token.expires_at && new Date(token.expires_at) < new Date()) return <span className={`${baseClasses} bg-yellow-100 text-yellow-800`}>Expired</span>;
    return <span className={`${baseClasses} bg-green-100 text-green-800`}>Active</span>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    if (role === 'admin') {
      return `${baseClasses} bg-red-100 text-red-800`;
    }
    return `${baseClasses} bg-blue-100 text-blue-800`;
  };

  const getStatusIcon = (isActive) => {
    return isActive
      ? <UserCheck className="w-5 h-5 text-green-600" />
      : <UserX className="w-5 h-5 text-red-600" />;
  };

  const getRequestStatusBadge = (status) => {
    const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    switch(status) {
      case 'completed':
        return `${baseClasses} bg-green-100 text-green-800`;
      case 'pending':
        return `${baseClasses} bg-yellow-100 text-yellow-800`;
      case 'expired':
        return `${baseClasses} bg-red-100 text-red-800`;
      default:
        return `${baseClasses} bg-gray-100 text-gray-800`;
    }
  };

  const getUploadStatusIcon = (request) => {
    if (request.status === 'completed') {
      return <CheckCircle className="w-5 h-5 text-green-600" />;
    }
    if (request.upload_started_at && !request.upload_completed_at) {
      return <Upload className="w-5 h-5 text-blue-600 animate-pulse" />;
    }
    if (request.upload_stalled_notified) {
      return <AlertTriangle className="w-5 h-5 text-amber-600" />;
    }
    return <Clock className="w-5 h-5 text-gray-400" />;
  };

  // Sorting functions
  const handleSort = (field, currentField, currentDirection, setField, setDirection) => {
    if (field === currentField) {
      setDirection(currentDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setField(field);
      setDirection('asc');
    }
  };

  const SortIcon = ({ field, currentField, currentDirection }) => {
    if (field !== currentField) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return currentDirection === 'asc'
      ? <ArrowUp className="w-4 h-4 text-primary-600" />
      : <ArrowDown className="w-4 h-4 text-primary-600" />;
  };

  // Filtered and sorted users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = [...users];

    // Search filter
    if (usersSearchTerm) {
      const searchLower = usersSearchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower)
      );
    }

    // Role filter
    if (usersRoleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === usersRoleFilter);
    }

    // Status filter
    if (usersStatusFilter === 'active') {
      filtered = filtered.filter(user => user.is_active);
    } else if (usersStatusFilter === 'inactive') {
      filtered = filtered.filter(user => !user.is_active);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal = a[usersSortField];
      let bVal = b[usersSortField];

      if (usersSortField === 'created_at' || usersSortField === 'last_login') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return usersSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return usersSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [users, usersSearchTerm, usersRoleFilter, usersStatusFilter, usersSortField, usersSortDirection]);

  // Filtered and sorted requests
  const filteredAndSortedRequests = useMemo(() => {
    let filtered = [...fileRequests];

    // Search filter
    if (requestsSearchTerm) {
      const searchLower = requestsSearchTerm.toLowerCase();
      filtered = filtered.filter(request =>
        request.subject.toLowerCase().includes(searchLower) ||
        request.recipient_name.toLowerCase().includes(searchLower) ||
        request.recipient_email.toLowerCase().includes(searchLower) ||
        request.created_by.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (requestsStatusFilter !== 'all') {
      filtered = filtered.filter(request => request.status === requestsStatusFilter);
    }

    // Sorting
    filtered.sort((a, b) => {
      let aVal = a[requestsSortField];
      let bVal = b[requestsSortField];

      if (requestsSortField.includes('_at') || requestsSortField === 'created_at' || requestsSortField === 'expires_at') {
        aVal = aVal ? new Date(aVal) : new Date(0);
        bVal = bVal ? new Date(bVal) : new Date(0);
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal ? bVal.toLowerCase() : '';
      }

      if (aVal < bVal) return requestsSortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return requestsSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [fileRequests, requestsSearchTerm, requestsStatusFilter, requestsSortField, requestsSortDirection]);

  // Pagination
  const paginatedUsers = useMemo(() => {
    const startIndex = (usersPage - 1) * itemsPerPage;
    return filteredAndSortedUsers.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedUsers, usersPage]);

  const paginatedRequests = useMemo(() => {
    const startIndex = (requestsPage - 1) * itemsPerPage;
    return filteredAndSortedRequests.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedRequests, requestsPage]);

  const usersTotalPages = Math.ceil(filteredAndSortedUsers.length / itemsPerPage);
  const requestsTotalPages = Math.ceil(filteredAndSortedRequests.length / itemsPerPage);

  const Pagination = ({ currentPage, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 sm:px-6 mt-4">
        <div className="flex flex-1 justify-between sm:hidden">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Page <span className="font-medium">{currentPage}</span> of <span className="font-medium">{totalPages}</span>
            </p>
          </div>
          <div>
            <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              {[...Array(totalPages)].map((_, idx) => (
                <button
                  key={idx + 1}
                  onClick={() => onPageChange(idx + 1)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                    currentPage === idx + 1
                      ? 'z-10 bg-primary-600 text-white focus:z-20 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                      : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0'
                  }`}
                >
                  {idx + 1}
                </button>
              ))}
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="spinner w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading admin panel...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if current user is admin
  if (currentUser && currentUser.role !== 'admin') {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <div className="card">
            <div className="p-8">
              <Shield className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h1 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h1>
              <p className="text-gray-600 mb-6">You don't have permission to access the admin panel.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Panel</h1>
              <p className="text-lg text-gray-600">
                Manage users and system settings
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <button
                onClick={() => setShowCreateForm(!showCreateForm)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-5 h-5" />
                <span>Add User</span>
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">{successMessage}</p>
          </div>
        )}

        {/* Confirmation Modal */}
        {confirmAction && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {confirmAction.type === 'delete' && 'Delete User'}
                {confirmAction.type === 'suspend' && 'Suspend User'}
                {confirmAction.type === 'unsuspend' && 'Activate User'}
                {confirmAction.type === 'reset-password' && 'Reset Password'}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                {confirmAction.type === 'delete' && `Are you sure you want to permanently delete "${confirmAction.userName}"? This action cannot be undone.`}
                {confirmAction.type === 'suspend' && `Are you sure you want to suspend "${confirmAction.userName}"? They will not be able to log in.`}
                {confirmAction.type === 'unsuspend' && `Are you sure you want to activate "${confirmAction.userName}"?`}
                {confirmAction.type === 'reset-password' && `Send a password reset email to "${confirmAction.userName}"? Their current password will be invalidated.`}
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setConfirmAction(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (confirmAction.type === 'delete') handleDeleteUser(confirmAction.userId);
                    if (confirmAction.type === 'suspend') handleToggleStatus(confirmAction.userId, true);
                    if (confirmAction.type === 'unsuspend') handleToggleStatus(confirmAction.userId, false);
                    if (confirmAction.type === 'reset-password') handleResetPassword(confirmAction.userId);
                  }}
                  disabled={actionLoading === confirmAction.userId}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md disabled:opacity-50 ${
                    confirmAction.type === 'delete' ? 'bg-red-600 hover:bg-red-700' : 'bg-black hover:bg-gray-800'
                  }`}
                >
                  {actionLoading === confirmAction.userId ? 'Processing...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('users')}
                className={`${
                  activeTab === 'users'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <Users className="w-5 h-5" />
                <span>Users</span>
              </button>
              <button
                onClick={() => setActiveTab('requests')}
                className={`${
                  activeTab === 'requests'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <FileText className="w-5 h-5" />
                <span>File Requests</span>
              </button>
              <button
                onClick={() => setActiveTab('tokens')}
                className={`${
                  activeTab === 'tokens'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
              >
                <Key className="w-5 h-5" />
                <span>API Tokens</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Users className="w-8 h-8 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {users.length}
                  </p>
                  <p className="text-sm text-gray-600">Total Users</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Shield className="w-8 h-8 text-red-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.role === 'admin').length}
                  </p>
                  <p className="text-sm text-gray-600">Administrators</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UserCheck className="w-8 h-8 text-green-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {users.filter(u => u.is_active).length}
                  </p>
                  <p className="text-sm text-gray-600">Active Users</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="w-8 h-8 text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-2xl font-bold text-gray-900">
                    {fileRequests.length}
                  </p>
                  <p className="text-sm text-gray-600">Total Requests</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Create User Form */}
        {showCreateForm && (
          <div className="card mb-8">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Create New User</h2>
              <p className="text-sm text-gray-600 mb-4">
                The user will receive an email with a secure link to set their password before they can log in.
              </p>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Username
                    </label>
                    <input
                      type="text"
                      required
                      value={newUser.username}
                      onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      required
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      value={newUser.role}
                      onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      <option value="user">User</option>
                      <option value="admin">Administrator</option>
                    </select>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="submit"
                    className="btn-primary"
                  >
                    Create User
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateForm(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Users List */}
        {activeTab === 'users' && (
        <div className="card">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">System Users</h2>

            {/* Filters and Search */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search users by name or email..."
                    value={usersSearchTerm}
                    onChange={(e) => {
                      setUsersSearchTerm(e.target.value);
                      setUsersPage(1);
                    }}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <select
                  value={usersRoleFilter}
                  onChange={(e) => {
                    setUsersRoleFilter(e.target.value);
                    setUsersPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>
              </div>
              <div>
                <select
                  value={usersStatusFilter}
                  onChange={(e) => {
                    setUsersStatusFilter(e.target.value);
                    setUsersPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* Sort Controls */}
            <div className="mb-4 flex items-center space-x-4 text-sm">
              <span className="text-gray-600">Sort by:</span>
              <button
                onClick={() => handleSort('username', usersSortField, usersSortDirection, setUsersSortField, setUsersSortDirection)}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Username</span>
                <SortIcon field="username" currentField={usersSortField} currentDirection={usersSortDirection} />
              </button>
              <button
                onClick={() => handleSort('email', usersSortField, usersSortDirection, setUsersSortField, setUsersSortDirection)}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Email</span>
                <SortIcon field="email" currentField={usersSortField} currentDirection={usersSortDirection} />
              </button>
              <button
                onClick={() => handleSort('created_at', usersSortField, usersSortDirection, setUsersSortField, setUsersSortDirection)}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Created</span>
                <SortIcon field="created_at" currentField={usersSortField} currentDirection={usersSortDirection} />
              </button>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              Showing {filteredAndSortedUsers.length} user{filteredAndSortedUsers.length !== 1 ? 's' : ''}
            </div>

            {filteredAndSortedUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                <p className="text-gray-600">Create your first user to get started.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paginatedUsers.map((user) => (
                  <div
                    key={user.id}
                    className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getStatusIcon(user.is_active)}
                          <h3 className="text-lg font-semibold text-gray-900">
                            {user.username}
                          </h3>
                          <span className={getRoleBadge(user.role)}>
                            {user.role}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Mail className="w-4 h-4" />
                            <span>{user.email}</span>
                          </div>

                          <div className="flex items-center space-x-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            <span>Created {formatDate(user.created_at)}</span>
                          </div>

                          {user.last_login ? (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4" />
                              <span>Last login {formatDate(user.last_login)}</span>
                            </div>
                          ) : !user.password_set ? (
                            <div className="flex items-center space-x-2 text-sm text-amber-600">
                              <Mail className="w-4 h-4" />
                              <span>Password setup pending</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <span>Never logged in</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {currentUser && user.id !== currentUser.id && (
                        <div className="flex items-center space-x-2 mt-4 md:mt-0 flex-shrink-0">
                          {/* Role toggle */}
                          <button
                            onClick={() => handleChangeRole(user.id, user.role === 'admin' ? 'user' : 'admin')}
                            disabled={actionLoading === user.id}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            title={user.role === 'admin' ? 'Demote to User' : 'Promote to Admin'}
                          >
                            <ShieldCheck className="w-3.5 h-3.5 mr-1" />
                            {user.role === 'admin' ? 'Make User' : 'Make Admin'}
                          </button>

                          {/* Reset Password */}
                          <button
                            onClick={() => setConfirmAction({ type: 'reset-password', userId: user.id, userName: user.username })}
                            disabled={actionLoading === user.id}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            title="Reset Password"
                          >
                            <RotateCcw className="w-3.5 h-3.5 mr-1" />
                            Reset Pwd
                          </button>

                          {/* Suspend/Unsuspend */}
                          <button
                            onClick={() => setConfirmAction({
                              type: user.is_active ? 'suspend' : 'unsuspend',
                              userId: user.id,
                              userName: user.username
                            })}
                            disabled={actionLoading === user.id}
                            className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border disabled:opacity-50 ${
                              user.is_active
                                ? 'border-amber-300 text-amber-700 bg-white hover:bg-amber-50'
                                : 'border-green-300 text-green-700 bg-white hover:bg-green-50'
                            }`}
                            title={user.is_active ? 'Suspend User' : 'Activate User'}
                          >
                            <Ban className="w-3.5 h-3.5 mr-1" />
                            {user.is_active ? 'Suspend' : 'Activate'}
                          </button>

                          {/* Delete */}
                          <button
                            onClick={() => setConfirmAction({ type: 'delete', userId: user.id, userName: user.username })}
                            disabled={actionLoading === user.id}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                            title="Delete User"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Pagination
              currentPage={usersPage}
              totalPages={usersTotalPages}
              onPageChange={setUsersPage}
            />
          </div>
        </div>
        )}

        {/* File Requests List */}
        {activeTab === 'requests' && (
        <div className="card">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">All File Requests</h2>

            {/* Filters and Search */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by subject, recipient, or creator..."
                    value={requestsSearchTerm}
                    onChange={(e) => {
                      setRequestsSearchTerm(e.target.value);
                      setRequestsPage(1);
                    }}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
              <div>
                <select
                  value={requestsStatusFilter}
                  onChange={(e) => {
                    setRequestsStatusFilter(e.target.value);
                    setRequestsPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                  <option value="expired">Expired</option>
                </select>
              </div>
            </div>

            {/* Sort Controls */}
            <div className="mb-4 flex items-center space-x-4 text-sm">
              <span className="text-gray-600">Sort by:</span>
              <button
                onClick={() => handleSort('created_at', requestsSortField, requestsSortDirection, setRequestsSortField, setRequestsSortDirection)}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Created</span>
                <SortIcon field="created_at" currentField={requestsSortField} currentDirection={requestsSortDirection} />
              </button>
              <button
                onClick={() => handleSort('subject', requestsSortField, requestsSortDirection, setRequestsSortField, setRequestsSortDirection)}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Subject</span>
                <SortIcon field="subject" currentField={requestsSortField} currentDirection={requestsSortDirection} />
              </button>
              <button
                onClick={() => handleSort('status', requestsSortField, requestsSortDirection, setRequestsSortField, setRequestsSortDirection)}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Status</span>
                <SortIcon field="status" currentField={requestsSortField} currentDirection={requestsSortDirection} />
              </button>
              <button
                onClick={() => handleSort('expires_at', requestsSortField, requestsSortDirection, setRequestsSortField, setRequestsSortDirection)}
                className="flex items-center space-x-1 text-gray-700 hover:text-primary-600"
              >
                <span>Expires</span>
                <SortIcon field="expires_at" currentField={requestsSortField} currentDirection={requestsSortDirection} />
              </button>
            </div>

            <div className="text-sm text-gray-600 mb-4">
              Showing {filteredAndSortedRequests.length} request{filteredAndSortedRequests.length !== 1 ? 's' : ''}
            </div>

            {filteredAndSortedRequests.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No file requests found</h3>
                <p className="text-gray-600">File requests will appear here when users create them.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {paginatedRequests.map((request) => (
                  <div
                    key={request.id}
                    className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          {getUploadStatusIcon(request)}
                          <h3 className="text-lg font-semibold text-gray-900">
                            {request.subject}
                          </h3>
                          <span className={getRequestStatusBadge(request.status)}>
                            {request.status}
                          </span>
                          {request.is_reusable && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              <RefreshCw className="w-3 h-3 mr-1" />
                              Reusable
                            </span>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                          <div className="flex items-start flex-col space-y-1">
                            <span className="text-xs font-medium text-gray-500">Recipient</span>
                            <div className="flex items-center space-x-2 text-sm text-gray-900">
                              <Mail className="w-4 h-4" />
                              <span>{request.recipient_name}</span>
                            </div>
                            <span className="text-xs text-gray-500">{request.recipient_email}</span>
                          </div>

                          <div className="flex items-start flex-col space-y-1">
                            <span className="text-xs font-medium text-gray-500">Created By</span>
                            <div className="flex items-center space-x-2 text-sm text-gray-900">
                              <Users className="w-4 h-4" />
                              <span>{request.created_by_user?.username || request.created_by}</span>
                            </div>
                            {request.created_by_user && (
                              <span className="text-xs text-gray-500">{request.created_by_user.email}</span>
                            )}
                          </div>

                          <div className="flex items-start flex-col space-y-1">
                            <span className="text-xs font-medium text-gray-500">Timeline</span>
                            <div className="flex items-center space-x-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4" />
                              <span>Created {formatDate(request.created_at)}</span>
                            </div>
                            <span className="text-xs text-gray-500">Expires {formatDate(request.expires_at)}</span>
                          </div>

                          {request.upload_started_at && (
                            <div className="flex items-start flex-col space-y-1">
                              <span className="text-xs font-medium text-gray-500">Upload Status</span>
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <Upload className="w-4 h-4" />
                                <span>Started {formatDate(request.upload_started_at)}</span>
                              </div>
                              {request.upload_completed_at && (
                                <span className="text-xs text-green-600">Completed {formatDate(request.upload_completed_at)}</span>
                              )}
                              {!request.upload_completed_at && request.upload_stalled_notified && (
                                <span className="text-xs text-amber-600">⚠ Stalled</span>
                              )}
                            </div>
                          )}

                          <div className="flex items-start flex-col space-y-1">
                            <span className="text-xs font-medium text-gray-500">Files</span>
                            <div className="flex items-center space-x-2 text-sm text-gray-900">
                              <FileText className="w-4 h-4" />
                              <span>{request.files_count} file{request.files_count !== 1 ? 's' : ''}</span>
                            </div>
                            {request.files_count > 0 && (
                              <button
                                onClick={() => handleViewFiles(request.id)}
                                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-primary-100 text-primary-700 hover:bg-primary-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500"
                              >
                                <Eye className="w-3 h-3 mr-1" />
                                View & Download
                              </button>
                            )}
                          </div>

                          <div className="flex items-start flex-col space-y-1">
                            <span className="text-xs font-medium text-gray-500">Notifications</span>
                            <div className="flex items-center space-x-3">
                              {request.upload_start_notified && (
                                <span className="text-xs text-green-600">✓ Start</span>
                              )}
                              {request.status === 'completed' && (
                                <span className="text-xs text-green-600">✓ Complete</span>
                              )}
                              {request.upload_stalled_notified && (
                                <span className="text-xs text-amber-600">⚠ Stalled</span>
                              )}
                              {!request.upload_start_notified && !request.upload_stalled_notified && request.status !== 'completed' && (
                                <span className="text-xs text-gray-400">None sent</span>
                              )}
                            </div>
                          </div>

                          {/* Metadata display */}
                          {request.metadata && Object.keys(request.metadata).length > 0 && (
                            <div className="flex items-start flex-col space-y-1">
                              <span className="text-xs font-medium text-gray-500">Metadata</span>
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(request.metadata).map(([key, value]) => (
                                  <span key={key} className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-purple-100 text-purple-800">
                                    <Tag className="w-3 h-3 mr-1" />
                                    {key}: {value}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Reusable toggle for admins */}
                          <div className="flex items-start flex-col space-y-1">
                            <span className="text-xs font-medium text-gray-500">Link Type</span>
                            {request.status !== 'expired' ? (
                              <button
                                onClick={() => handleToggleReusable(request.id, request.is_reusable)}
                                className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 ${
                                  request.is_reusable
                                    ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                title={request.is_reusable ? 'Click to make single-use' : 'Click to make reusable'}
                              >
                                <RefreshCw className="w-3 h-3 mr-1" />
                                {request.is_reusable ? 'Reusable' : 'Single-use'}
                              </button>
                            ) : (
                              <span className="text-xs text-gray-500">
                                {request.is_reusable ? 'Was reusable' : 'Single-use'}
                              </span>
                            )}
                          </div>

                          {/* Actions for admins */}
                          <div className="flex items-start flex-col space-y-1">
                            <span className="text-xs font-medium text-gray-500">Actions</span>
                            <div className="flex flex-wrap gap-1">
                              {request.upload_url && (
                                <button
                                  onClick={() => handleCopyLink(request.upload_url, request.id)}
                                  className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 ${
                                    copiedLink === request.id
                                      ? 'bg-security-100 text-security-700'
                                      : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  }`}
                                  title="Copy upload link"
                                >
                                  {copiedLink === request.id ? (
                                    <>
                                      <Check className="w-3 h-3 mr-1" />
                                      Copied!
                                    </>
                                  ) : (
                                    <>
                                      <Link2 className="w-3 h-3 mr-1" />
                                      Copy Link
                                    </>
                                  )}
                                </button>
                              )}
                              {(request.is_reusable || request.status !== 'completed') && (
                                <button
                                  onClick={() => setExtendingRequest(request.id)}
                                  className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-md bg-green-100 text-green-700 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500"
                                  title="Extend expiration"
                                >
                                  <CalendarPlus className="w-3 h-3 mr-1" />
                                  Extend
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <Pagination
              currentPage={requestsPage}
              totalPages={requestsTotalPages}
              onPageChange={setRequestsPage}
            />
          </div>
        </div>
        )}

        {/* Extend Expiration Modal */}
        {extendingRequest && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Extend Expiration
                  </h3>
                  <button
                    onClick={() => {
                      setExtendingRequest(null);
                      setExtendHours(72);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Extend by:
                  </label>
                  <select
                    value={extendHours}
                    onChange={(e) => setExtendHours(parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value={24}>24 hours</option>
                    <option value={48}>2 days</option>
                    <option value={72}>3 days</option>
                    <option value={168}>1 week</option>
                    <option value={336}>2 weeks</option>
                    <option value={720}>30 days</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    {fileRequests.find(r => r.id === extendingRequest)?.status === 'expired'
                      ? 'This link has expired. Extending will reactivate it from now.'
                      : 'Time will be added to the current expiration date.'}
                  </p>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => handleExtendExpiration(extendingRequest)}
                    className="flex-1 btn-primary"
                  >
                    Extend
                  </button>
                  <button
                    onClick={() => {
                      setExtendingRequest(null);
                      setExtendHours(72);
                    }}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Files Modal */}
        {viewingFiles && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Uploaded Files
                  </h3>
                  <button
                    onClick={() => {
                      setViewingFiles(null);
                      setFiles([]);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XCircle className="w-6 h-6" />
                  </button>
                </div>

                {filesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="spinner w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full mr-3"></div>
                    <span className="text-gray-600">Loading files...</span>
                  </div>
                ) : files.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No files found</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {files.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center space-x-3">
                          <File className="w-5 h-5 text-gray-400" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {file.filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatFileSize(file.size)} {file.uploaded_at && `• ${formatDate(file.uploaded_at)}`}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadFile(file.id, file.filename)}
                          className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <Download className="w-3 h-3 mr-1" />
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* API Tokens Tab */}
        {activeTab === 'tokens' && (
          <div>
            {/* Newly created token banner */}
            {newlyCreatedToken && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-yellow-800 mb-1">
                      Save your API token now - it won't be shown again!
                    </h4>
                    <div className="flex items-center space-x-2 mt-2">
                      <code className="flex-1 text-sm bg-yellow-100 text-yellow-900 px-3 py-2 rounded font-mono break-all">
                        {newlyCreatedToken}
                      </code>
                      <button
                        onClick={() => copyToClipboard(newlyCreatedToken)}
                        className="px-3 py-2 bg-yellow-200 hover:bg-yellow-300 rounded text-yellow-800 transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                    <button
                      onClick={() => setNewlyCreatedToken(null)}
                      className="mt-3 text-sm text-yellow-700 hover:text-yellow-900 underline"
                    >
                      I've saved this token, dismiss
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">API Tokens</h2>
              <button
                onClick={() => setShowCreateTokenForm(!showCreateTokenForm)}
                className="btn-primary flex items-center space-x-2"
              >
                <Plus className="w-4 h-4" />
                <span>Create Token</span>
              </button>
            </div>

            {/* Create Token Form */}
            {showCreateTokenForm && (
              <div className="card mb-6">
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New API Token</h3>
                  <form onSubmit={handleCreateToken} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Token Name</label>
                      <input
                        type="text"
                        value={newToken.name}
                        onChange={(e) => setNewToken({ ...newToken, name: e.target.value })}
                        placeholder="e.g. CRM Integration, Client Portal"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Expires In (days, optional)</label>
                      <input
                        type="number"
                        value={newToken.expires_in_days}
                        onChange={(e) => setNewToken({ ...newToken, expires_in_days: e.target.value })}
                        placeholder="Leave empty for no expiration"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
                      <select
                        value={newToken.scope}
                        onChange={(e) => setNewToken({ ...newToken, scope: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      >
                        <option value="user">User - can only access its own requests</option>
                        <option value="admin">Admin - full access to all requests</option>
                      </select>
                    </div>

                    <div className="border-t pt-4">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Embed Styling (optional)</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Primary Color</label>
                          <div className="flex items-center space-x-2">
                            <input
                              type="color"
                              value={newToken.embed_primary_color}
                              onChange={(e) => setNewToken({ ...newToken, embed_primary_color: e.target.value })}
                              className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                            />
                            <input
                              type="text"
                              value={newToken.embed_primary_color}
                              onChange={(e) => setNewToken({ ...newToken, embed_primary_color: e.target.value })}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                              pattern="^#[0-9a-fA-F]{6}$"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Header Text</label>
                          <input
                            type="text"
                            value={newToken.embed_header_text}
                            onChange={(e) => setNewToken({ ...newToken, embed_header_text: e.target.value })}
                            placeholder="e.g. Upload Documents"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={newToken.embed_show_logo}
                            onChange={(e) => setNewToken({ ...newToken, embed_show_logo: e.target.checked })}
                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-gray-700">Show logo in embedded form</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex space-x-3">
                      <button type="submit" className="btn-primary">
                        Create Token
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateTokenForm(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {/* Token List */}
            <div className="card">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prefix</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Scope</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {apiTokens.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                          No API tokens created yet.
                        </td>
                      </tr>
                    ) : (
                      apiTokens.map((token) => (
                        <tr key={token.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <Key className="w-4 h-4 text-gray-400" />
                              <span className="text-sm font-medium text-gray-900">{token.name}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <code className="text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">{token.token_prefix}...</code>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {token.scope === 'admin' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">Admin</span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">User</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {getTokenStatusBadge(token)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatDate(token.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                            {token.last_used_at ? formatDate(token.last_used_at) : 'Never'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {token.is_active && (
                              <button
                                onClick={() => handleRevokeToken(token.id)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium flex items-center space-x-1"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Revoke</span>
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
}
